import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Logout as LogoutIcon,
  Shield as ShieldIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearAdminToken } from '../../services/adminApi';

const DRAWER_W = 240;
const DRAWER_COLLAPSED = 64;

const navItems = [
  { label: 'Dashboard', icon: <DashboardIcon />, route: '/admin' },
  { label: 'Servidores', icon: <PeopleIcon />, route: '/admin/servidores' },
  { label: 'Documentos', icon: <DescriptionIcon />, route: '/admin/documentos' },
];

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(true);
  const adminUser = (() => {
    try { return JSON.parse(localStorage.getItem('admin_user') || '{}'); }
    catch { return {}; }
  })();

  const logout = () => {
    clearAdminToken();
    navigate('/admin/login');
  };

  const drawerW = expanded ? DRAWER_W : DRAWER_COLLAPSED;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8F9FA' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerW,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerW,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: '#0D1B2A',
            color: 'white',
            transition: 'width 0.25s ease',
            overflow: 'hidden',
          },
        }}
      >
        {/* Logo area */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 2,
            minHeight: 64,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: '#1565C0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ShieldIcon sx={{ fontSize: 20, color: 'white' }} />
          </Box>
          {expanded && (
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" fontWeight={700} noWrap sx={{ color: 'white' }}>
                SINTET Admin
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Sistema de Gestão
              </Typography>
            </Box>
          )}
          <Box sx={{ ml: 'auto' }}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}
            >
              {expanded ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Nav */}
        <List sx={{ px: 1, py: 1.5, flex: 1 }}>
          {navItems.map((item) => {
            const active = location.pathname === item.route ||
              (item.route !== '/admin' && location.pathname.startsWith(item.route));
            return (
              <ListItem key={item.route} disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={!expanded ? item.label : ''} placement="right">
                  <ListItemButton
                    onClick={() => navigate(item.route)}
                    sx={{
                      borderRadius: 2,
                      minHeight: 44,
                      px: expanded ? 2 : 1.5,
                      bgcolor: active ? 'rgba(21,101,192,0.9)' : 'transparent',
                      '&:hover': { bgcolor: active ? 'rgba(21,101,192,0.9)' : 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: expanded ? 1.5 : 0,
                        color: active ? 'white' : 'rgba(255,255,255,0.55)',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {expanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: active ? 700 : 500,
                          color: active ? 'white' : 'rgba(255,255,255,0.75)',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        {/* User + Logout */}
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', p: 1.5 }}>
          {expanded ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#1565C0', fontSize: 14 }}>
                {(adminUser.nome || adminUser.username || 'A').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ color: 'white' }}>
                  {adminUser.nome || adminUser.username}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {adminUser.role}
                </Typography>
              </Box>
            </Box>
          ) : null}
          <Tooltip title="Sair" placement="right">
            <ListItemButton
              onClick={logout}
              sx={{
                borderRadius: 2,
                color: 'rgba(255,100,100,0.8)',
                px: expanded ? 1.5 : 1.5,
                '&:hover': { bgcolor: 'rgba(255,50,50,0.1)', color: '#ff6b6b' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 1.5 : 0, color: 'inherit' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary="Sair"
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: 'inherit' }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          p: 4,
          overflowY: 'auto',
          transition: 'margin 0.25s ease',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;